import { FC, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Typography,
  Stack,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@mui/material";
import {
  Bookmark as BookmarkIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesEmployeeAbsenceRecordsWrite } from "src/utils/RoleBasedSecurity/permissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import BalanceStatusChip from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsBalances/BalanceStatus/BalanceStatusChip";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

interface IAbsenceTypeItemProps {
  employeeId: string;
  data: IAbsenceType;
  forceExpand?: boolean;
  isSecondary?: boolean;
}

const AbsenceTypeItem: FC<IAbsenceTypeItemProps> = ({
  employeeId,
  data,
  forceExpand = false,
  isSecondary = false,
}) => {
  const basePath = useBasePath();
  const logStatus = useLogStatus();
  const canWriteAbsenceRecords = usePermission(
    absencesEmployeeAbsenceRecordsWrite
  );
  const [expanded, setExpanded] = useState(true);

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceTypeItem.tsx",
    });
  }

  if (!data) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No balances data provided.",
      file: "AbsenceTypeItem.tsx",
    });
  }

  const {
    totalAvailableTimeAmount,
    confirmedPastUsedTimeAmount,
    grantedTimeAmount,
    reservedTimeAmount,
    overdueAvailableTimeAmount,
  } = data.status;

  useEffect(() => {
    setExpanded(forceExpand);
  }, [forceExpand]);

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Accordion
      variant="flat"
      disableGutters
      expanded={expanded}
      onChange={toggleExpand}
      data-testid="absence-type-list__item"
    >
      <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
        <Stack direction="row" alignItems="center">
          <BookmarkIcon
            htmlColor={data.color}
            sx={{ marginRight: "0.75rem" }}
          />
          <Typography
            variant="subtitle1"
            data-testid="absence-type-list__item-name"
          >
            {data.name}
          </Typography>
          {data.id && canWriteAbsenceRecords && (
            <Box sx={{ ml: "auto" }}>
              <RouterLink
                to={`${basePath}/absences/modify-balance/${employeeId}/${data.id}`}
              >
                <Button
                  variant="outlined"
                  data-testid="absence-balance-status__button-edit"
                >
                  {__("Edytuj")}
                </Button>
              </RouterLink>
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack
          direction="row"
          alignItems="baseline"
          useFlexGap
          gap={1}
          sx={{ marginLeft: "2.625rem", paddingBottom: "1rem" }}
        >
          {isSecondary && (
            <>
              <Typography variant="body1" color="text.secondary">
                {__("Wymiar zawiera się w:")} <strong>{data.parentName}</strong>
              </Typography>
              &bull;
            </>
          )}
          <Link
            to={`${basePath}/absences/type-absence-requests/${employeeId}/${data.id}`}
            component={RouterLink}
            fontSize="1rem"
            color="text.secondary"
            data-testid="link-to-type-absence-requests"
          >
            {__("Złożone wnioski")}
          </Link>
          &bull;
          <Link
            to={`${basePath}/absences/absence-type-history/${employeeId}/${data.id}`}
            component={RouterLink}
            fontSize="1rem"
            color="text.secondary"
            data-testid="link-to-absence-type-history"
          >
            {__("Pełna historia")}
          </Link>
        </Stack>
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={2}
          sx={{ marginLeft: "2.625rem", paddingBottom: "1rem" }}
        >
          {totalAvailableTimeAmount && (
            <BalanceStatusChip
              label="Dostępne"
              status={totalAvailableTimeAmount}
            />
          )}
          {overdueAvailableTimeAmount && (
            <BalanceStatusChip
              label="Zaległe"
              status={overdueAvailableTimeAmount}
            />
          )}
          {grantedTimeAmount && (
            <BalanceStatusChip
              label="Przyznane w okresie"
              status={grantedTimeAmount}
            />
          )}
          {confirmedPastUsedTimeAmount && (
            <BalanceStatusChip
              label="Wykorzystane"
              status={confirmedPastUsedTimeAmount}
              helpText={__(
                "Liczba dni nieobecności wykorzystanych do tej pory."
              )}
            />
          )}
          {reservedTimeAmount && (
            <BalanceStatusChip
              label="Zarezerwowane"
              status={reservedTimeAmount}
              helpText={__(
                "Liczba dni zarezerwowanych, tj. na które złożono wniosek, ale nieobecność jeszcze się nie odbyła."
              )}
            />
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default AbsenceTypeItem;
